import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ADD_SHARED_AGENT,
  LIST_SHARED_AGENTS,
  EDIT_SHARED_AGENT,
} from '~/services';
import { useMutation, useQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import UserDialog from './UserDialog'; // Import the new UserDialog component
import {
  createTable,
  ColumnDef,
  getCoreRowModel,
  flexRender,
  useReactTable,
} from '@tanstack/react-table';
import { useLayoutContext } from '~/components/layout/Layout';

interface RolPermissionsProps {
  searchValue: string;
  selectedFilter: string;
  selectedSort: string;
  isAddClicked: boolean;
  setIsAddClicked: (value: boolean) => void;
}

interface User {
  name: string;
  role: string;
  email: string;
  status: string;
  _id: string;
  phone_number?: string;
  notify_user?: boolean;
}

export default function Users({
  searchValue,
  selectedFilter,
  selectedSort,
  isAddClicked,
  setIsAddClicked,
}: RolPermissionsProps) {
  const [t] = useTranslation('teams');
  const { agentId } = useParams();
  const { data, loading } = useQuery(LIST_SHARED_AGENTS, {
    variables: { data: { agent_id: agentId } },
  });

  const [dropdownVisible, setDropdownVisible] = useState<number | null>(null);
  const [dialogRef, setDialogRef] = useState<HTMLDialogElement | null>(null);
  const [addSharedAgent] = useMutation(ADD_SHARED_AGENT);
  const [editSharedAgent] = useMutation(EDIT_SHARED_AGENT);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [phone_number, setPhoneNumber] = useState('');
  const [notify_user, setNotifyUser] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [Users, setUser] = useState<User[]>([]);
  const [userFilter, setUserFilter] = useState(Users);

  const { renderToast } = useLayoutContext();
  const navigate = useNavigate();

  const viewUser = (userId: string) => {
    navigate(`/agent/${agentId}/teams/users/${userId}`);
  };

  useEffect(() => {
    if (data && data.list_shared_agents && data.list_shared_agents.data) {
      setUser(data.list_shared_agents.data);
    }
  }, [data]);

  const editUser = (user: User) => {
    setIsEditModal(true);
    setName(user.name);
    setEmail(user.email);
    setRole(user.role);
    setPhoneNumber(user.phone_number || '');
    setNotifyUser(user.notify_user || false);
    openDialog();
  };

  const deleteUser = async (user: User) => {
    try {
      const res = await editSharedAgent({
        variables: {
          data: {
            action: 'DELETE',
            agent_id: agentId,
            email: user.email,
            role: user.role,
          },
        },
      });
      setUser(Users.filter((miUser) => miUser._id !== user._id));
      renderToast({ message: t('user_deleted_successfully'), type: 'success' });
    } catch (error) {
      renderToast({ message: t('error_deleting_user'), type: 'error' });
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await addSharedAgent({
        variables: {
          data: {
            agent_id: agentId,
            users: [
              {
                email: email,
                role: role,
                name: name,
                phone_number: phone_number,
                notify_user: notify_user,
              },
            ],
          },
        },
      });
      if (res.data && res.data.shared_agent && res.data.shared_agent.data) {
        setUser([
          ...Users,
          {
            _id: res.data.shared_agent.data[0],
            name,
            email,
            role,
            phone_number,
            notify_user,
            status: 'PENDING',
          },
        ]);
        renderToast({ message: t('user_added_successfully'), type: 'success' });
      }
    } catch (error) {
      renderToast({
        message: t('error_adding_user', { email }),
        type: 'error',
      });
    }
    closeDialog();
  };

  useEffect(() => {
    let list = [...Users];

    const filterField = (selectedFilter || 'name') as keyof User;
    const term = searchValue.trim().toLowerCase();
    if (term) {
      list = list.filter((u) =>
        String(u[filterField] ?? '').toLowerCase().includes(term),
      );
    }

    const sortField = (selectedSort || 'name') as keyof User;
    list = list.sort((a, b) =>
      String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? '')),
    );

    setUserFilter(list);
  }, [Users, searchValue, selectedFilter, selectedSort]);

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const res = await editSharedAgent({
        variables: {
          data: {
            action: 'EDIT',
            agent_id: agentId,
            email: email,
            role: role,
            name: name,
            phone_number: phone_number,
            notify_user: notify_user,
          },
        },
      });
      if (
        res.data &&
        res.data.edit_shared_agent &&
        res.data.edit_shared_agent.data
      ) {
        setUser(
          Users.map((miUser) =>
            miUser.email === email
              ? { ...miUser, name, email, role, phone_number, notify_user }
              : miUser,
          ),
        );

        if (res.data.edit_shared_agent.hasError)
          renderToast({ message: t('error_editing_user'), type: 'error' });
        else
          renderToast({
            message: t('user_edited_successfully'),
            type: 'success',
          });
      }
    } catch (error) {
      renderToast({ message: t('error_editing_user'), type: 'error' });
    }
    closeDialog();
  };

  const toggleDropdown = (index: number) => {
    setDropdownVisible(dropdownVisible === index ? null : index);
  };

  const openDialog = () => {
    if (dialogRef) {
      dialogRef.showModal();
    }
    setIsAddClicked(false);
  };

  const closeDialog = () => {
    if (dialogRef) {
      dialogRef.close();
    }
    setIsEditModal(false);
    setIsAddClicked(false);
    setDropdownVisible(null);
    setName('');
    setEmail('');
    setRole('');
    setPhoneNumber('');
    setNotifyUser(false);
  };

  // Define columns
  const columns: ColumnDef<User>[] = [
    {
      header: t('name'),
      accessorKey: 'name',
    },
    {
      header: t('status'),
      accessorKey: 'status',
    },
    {
      header: t('role'),
      accessorKey: 'role',
      cell: ({ row }) => {
        const role = row.original.role?.toUpperCase();
        const color = role === 'ADMIN' ? 'badge-error' : role === 'COLLABORATOR' ? 'badge-success' : 'badge-warning';
        return (
          <span className={`badge ${color} badge-sm`}>{row.original.role}</span>
        );
      },
    },
    {
      header: t('email'),
      accessorKey: 'email',
    },
    {
      header: t('phone_number'),
      accessorKey: 'phone_number',
      cell: ({ row }) =>
        row.original.phone_number ? (
          <span className={row.original.notify_user ? 'text-primary' : ''}>
            {row.original.phone_number}
          </span>
        ) : null,
    },
    {
      header: t('actions'),
      cell: ({ row }) => (
        <div className="relative">
          <button onClick={() => toggleDropdown(row.index)} className="btn btn-ghost btn-xs">
            ⋮
          </button>
          {dropdownVisible === row.index && (
            <div className="dropdown-content menu absolute right-0 z-10 mt-1 w-44 rounded-box bg-base-100 p-2 shadow">
              <li>
                <button
                  onClick={() => {
                    viewUser(row.original._id);
                    setDropdownVisible(null);
                  }}
                >
                  {t('view')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    editUser(row.original);
                    setDropdownVisible(null);
                  }}
                >
                  {t('edit')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    deleteUser(row.original);
                    setDropdownVisible(null);
                  }}
                >
                  {t('delete')}
                </button>
              </li>
            </div>
          )}
        </div>
      ),
    },
  ];

  // Initialize the table
  const table = useReactTable({
    data: userFilter,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full">
      {loading && <div className="text-sm opacity-70">{t('loading')}</div>}

      <div className="overflow-x-auto rounded-box bg-base-100 shadow">
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog
        isOpen={isAddClicked || isEditModal}
        isEditModal={isEditModal}
        name={name}
        email={email}
        role={role}
        phone_number={phone_number}
        notify_user={notify_user}
        setName={setName}
        setEmail={setEmail}
        setRole={setRole}
        setPhoneNumber={setPhoneNumber}
        setNotifyUser={setNotifyUser}
        handleSubmit={isEditModal ? handleEditSubmit : handleAdd}
        closeDialog={closeDialog}
      />
    </div>
  );
}
